import React, { useRef } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import TOUCH_TARGET_PX from '%app/js/common/constants/touchTarget';
import {
    ACTION_HEIGHT_PX,
    ACTIONS_GAP_SPACING,
    BODY_COLUMN_GAP_SPACING,
    BODY_ROW_GAP_SPACING,
    COMPACT_LAYOUT,
    COMPACT_NOTICE_PADDING_SPACING,
    COMPACT_SKIP_OFFSET_PX,
    FIRST_LINE_OFFSET_PX,
    INFO_ICON_SIZE_PX,
    LINE_HEIGHT_PX,
    MESSAGE_MIN_WIDTH_PX,
    NOTICE_GAP_SPACING,
    NOTICE_PADDING_LEFT_SPACING,
    NOTICE_PADDING_SPACING,
    PAGE_NOTICE_TITLE_ID,
    SKIP_ICON_SIZE_PX,
    SKIP_PADDING_PX,
} from '%app/js/common/constants/pageNoticeLayout';
import dismissKeepingKeyboardFocus from '%app/js/common/services/dismissKeepingKeyboardFocus';
import usePageNotice from '%app/js/common/services/usePageNotice';
import { NOTICE_AREA_PUBLIC } from '%app/js/common/services/pageNotices';
import { CONTENT_COLUMN_SX } from '../config/layout';
import FOCUS_RING_SX from './focusRingSx';
import { CONTROL_BORDER, STRONG_BORDER_COLOR } from './highContrastSx';
import MaterialSymbol from './MaterialSymbol';
import COMPACT_TOUCH_TARGET_SX from './touchTargetSx';

const COMPACT_SKIP_OFFSET = `-${COMPACT_SKIP_OFFSET_PX}px`;
const FIRST_LINE_OFFSET = `${FIRST_LINE_OFFSET_PX}px`;

const SPACE_SX = {
    ...CONTENT_COLUMN_SX,
    p: 2,
};

const NOTICE_SX = {
    display: { xs: 'grid', sm: 'flex' },
    gridTemplateColumns: 'auto minmax(0, 1fr) auto',
    gridTemplateAreas: COMPACT_LAYOUT,
    alignItems: { xs: 'start', sm: 'flex-start' },
    columnGap: NOTICE_GAP_SPACING,
    rowGap: NOTICE_GAP_SPACING,
    py: { xs: COMPACT_NOTICE_PADDING_SPACING, sm: NOTICE_PADDING_SPACING },
    pr: { xs: COMPACT_NOTICE_PADDING_SPACING, sm: NOTICE_PADDING_SPACING },
    pl: NOTICE_PADDING_LEFT_SPACING,
    border: CONTROL_BORDER,
    borderColor: STRONG_BORDER_COLOR,
    borderRadius: theme => `${theme.shape.borderRadiusLarge}px`,
    bgcolor: 'background.paper',
    color: 'text.primary',
};

const ICON_SX = {
    gridArea: 'icon',
    flexShrink: 0,
    mt: { xs: 0, sm: FIRST_LINE_OFFSET },
    color: 'primary.text',
};

const BODY_SX = {
    display: { xs: 'contents', sm: 'flex' },
    flex: 1,
    minWidth: 0,
    flexWrap: 'wrap',
    alignItems: 'flex-start',
    justifyContent: 'flex-end',
    columnGap: BODY_COLUMN_GAP_SPACING,
    rowGap: BODY_ROW_GAP_SPACING,
};

const MESSAGE_SX = {
    gridArea: 'message',
    flex: `1 1 ${MESSAGE_MIN_WIDTH_PX}px`,
    minWidth: 0,
    py: { xs: 0, sm: FIRST_LINE_OFFSET },
};

const LINE_SX = {
    display: 'block',
    lineHeight: `${LINE_HEIGHT_PX}px`,
};

const TEXT_SX = {
    ...LINE_SX,
    color: 'text.secondary',
};

const ACTIONS_SX = {
    gridArea: 'actions',
    display: 'flex',
    flexWrap: { xs: 'nowrap', sm: 'wrap' },
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: ACTIONS_GAP_SPACING,
};

const ACTION_FOCUS_RING_SX = {
    ...FOCUS_RING_SX,
    outlineColor: 'primary.text',
};

const ACTION_SX = {
    minHeight: { xs: TOUCH_TARGET_PX, sm: ACTION_HEIGHT_PX },
    textTransform: 'none',
    borderRadius: 2,
    '&:focus-visible': ACTION_FOCUS_RING_SX,
};

const FEEDBACK_LINK_SX = [
    ACTION_SX,
    {
        minWidth: 0,
        justifyContent: { xs: 'flex-start', sm: 'center' },
        textAlign: { xs: 'left', sm: 'center' },
        color: 'primary.text',
    },
];

const CONFIRM_SX = [
    ACTION_SX,
    {
        flexShrink: 0,
        bgcolor: 'primary.text',
    },
];

const SKIP_SX = {
    gridArea: 'skip',
    flexShrink: 0,
    p: `${SKIP_PADDING_PX}px`,
    ...COMPACT_TOUCH_TARGET_SX,
    mt: { xs: COMPACT_SKIP_OFFSET, sm: 0 },
    mr: { xs: COMPACT_SKIP_OFFSET, sm: 0 },
    mb: { xs: COMPACT_SKIP_OFFSET, sm: 0 },
    color: 'text.secondary',
    '&:focus-visible': ACTION_FOCUS_RING_SX,
};

function PageNotice() {
    const notice = usePageNotice(NOTICE_AREA_PUBLIC);
    const spaceRef = useRef(null);

    if (notice === null) {
        return null;
    }

    return (
        <Box ref={spaceRef} sx={SPACE_SX}>
            <Box component="section" aria-labelledby={PAGE_NOTICE_TITLE_ID} sx={NOTICE_SX}>
                <MaterialSymbol name="info" size={INFO_ICON_SIZE_PX} sx={ICON_SX} />
                <Box sx={BODY_SX}>
                    <Box sx={MESSAGE_SX}>
                        <Typography id={PAGE_NOTICE_TITLE_ID} variant="titleS" component="p" sx={LINE_SX}>
                            {notice.title}
                        </Typography>
                        {notice.text !== null && (
                            <Typography variant="bodyS" sx={TEXT_SX}>
                                {notice.text}
                            </Typography>
                        )}
                    </Box>
                    <Box sx={ACTIONS_SX}>
                        {notice.link !== null && (
                            <Button component="a" href={notice.link.url} size="small" sx={FEEDBACK_LINK_SX}>
                                {notice.link.label}
                            </Button>
                        )}
                        <Button
                            variant="contained"
                            size="small"
                            disableElevation
                            onClick={dismissKeepingKeyboardFocus(notice.confirm, spaceRef)}
                            sx={CONFIRM_SX}
                        >
                            {notice.confirmLabel}
                        </Button>
                    </Box>
                </Box>
                <IconButton
                    size="small"
                    aria-label={notice.skipLabel}
                    title={notice.skipLabel}
                    onClick={dismissKeepingKeyboardFocus(notice.skip, spaceRef)}
                    sx={SKIP_SX}
                >
                    <MaterialSymbol name="close" size={SKIP_ICON_SIZE_PX} />
                </IconButton>
            </Box>
        </Box>
    );
}

export default PageNotice;
